<!-- src/apps/secret/components/form/SecretForm.vue -->

<script setup lang="ts">
  import BasicFormAlerts from '@/shared/components/forms/BasicFormAlerts.vue';
  import OIcon from '@/shared/components/icons/OIcon.vue';
  import SplitButton from '@/shared/components/ui/SplitButton.vue';
  import { useDomainContext } from '@/shared/composables/useDomainContext';
  import { usePrivacyOptions } from '@/shared/composables/usePrivacyOptions';
  import { useSecretConcealer } from '@/shared/composables/useSecretConcealer';
  import { NEUTRAL_BRAND_DEFAULTS } from '@/shared/constants/brand';
  import { useAuthStore } from '@/shared/stores/authStore';
  import { useBootstrapStore } from '@/shared/stores/bootstrapStore';
  import {
    DEFAULT_BUTTON_TEXT_LIGHT,
    DEFAULT_CORNER_CLASS,
  } from '@/shared/stores/identityStore';
  import { useLocalReceiptStore } from '@/shared/stores/localReceiptStore';
  import { type LocalReceipt } from '@/types/ui/local-receipt';
  import { useMediaQuery } from '@vueuse/core';
  import { nanoid } from 'nanoid';
  import { storeToRefs } from 'pinia';
  import { computed, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useRouter } from 'vue-router';

  const { t } = useI18n();

  import AttachmentPicker from './AttachmentPicker.vue';
  import SecretContentInputArea from './SecretContentInputArea.vue';

  import {
    useAttachments,
    type UploadedAttachment,
  } from '@/apps/secret/composables/useAttachments';
  import { storeAttachmentFragment } from '@/apps/secret/composables/useAttachmentFragmentStore';
  import { useNotificationsStore } from '@/shared/stores/notificationsStore';

  export interface Props {
    enabled?: boolean;
    withRecipient?: boolean;
    withAsterisk?: boolean;
    withGenerate?: boolean;
    withExpiry?: boolean;
    cornerClass?: string;
    primaryColor?: string;
    buttonTextLight?: boolean;
    /** When true, form stays on page after creation instead of navigating to receipt */
    workspaceMode?: boolean;
    /**
     * When true, the outer card chrome (border/shadow/gradient) is dropped so
     * a parent frame (e.g. the landing TerminalWindow) provides it instead.
     */
    chromeless?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    enabled: true,
    withRecipient: false,
    withAsterisk: false,
    withGenerate: false,
    withExpiry: true,
    cornerClass: DEFAULT_CORNER_CLASS,
    primaryColor: NEUTRAL_BRAND_DEFAULTS.primary_color,
    buttonTextLight: DEFAULT_BUTTON_TEXT_LIGHT,
    workspaceMode: false,
    chromeless: false,
  });

  const emit = defineEmits<{
    /** Emitted after successful secret creation with the response data */
    (e: 'created', response: LocalReceipt): void;
  }>();

  const router = useRouter();
  const localReceiptStore = useLocalReceiptStore();
  const showProTip = ref(props.withAsterisk);

  // Get passphrase configuration for UI hints
  const bootstrapStore = useBootstrapStore();
  const {
    secret_options: secretOptions,
    domain_strategy: domainStrategy,
    display_domain: displayDomain,
  } = storeToRefs(bootstrapStore);

  const passphraseConfig = computed(() => secretOptions.value?.passphrase);
  const isPassphraseRequired = computed(() => passphraseConfig.value?.required || false);

  // Recipient field is shown only when the route opts in (withRecipient) AND the
  // ui.capabilities.recipient flag is not explicitly disabled. An unset flag
  // (undefined) is treated as enabled, matching the config default of true.
  const { uiCapabilities } = storeToRefs(bootstrapStore);
  const showRecipient = computed(
    () => props.withRecipient && uiCapabilities.value?.recipient !== false
  );

  // --- E2EE file attachments -------------------------------------------------
  // The picker only appears when object storage is actually configured
  // (bootstrap flag), and only for the create-link action (not generated
  // passwords). Files are encrypted client-side and uploaded AFTER the secret
  // is created, so the flow hangs off onSuccess below.
  const authStore = useAuthStore();
  const notifications = useNotificationsStore();
  const { uploadAttachment, buildAttachmentFragment } = useAttachments();
  const attachmentsConfig = computed(
    () =>
      (secretOptions.value as { attachments?: { enabled?: boolean; max_size?: number } } | undefined)
        ?.attachments
  );
  const attachmentsEnabled = computed(() => attachmentsConfig.value?.enabled === true);
  const attachmentMaxSize = computed(() => Number(attachmentsConfig.value?.max_size) || 26_214_400);
  const selectedFiles = ref<File[]>([]);
  const attachmentProgress = ref<number[]>([]);
  const uploadingAttachments = ref(false);

  // Encrypt + upload each selected file to the just-created receipt, then stash
  // the id→key fragment (the E2EE keys) locally so the receipt page can append
  // it to the share link. Never throws into the creation flow: a failed upload
  // leaves the secret intact and simply omits the file from the link.
  async function uploadSelectedAttachments(receiptId: string): Promise<void> {
    if (!attachmentsEnabled.value || !selectedFiles.value.length) return;
    const isGuest = authStore.isAuthenticated !== true;
    uploadingAttachments.value = true;
    attachmentProgress.value = selectedFiles.value.map(() => 0);
    try {
      const uploaded: UploadedAttachment[] = [];
      for (let i = 0; i < selectedFiles.value.length; i++) {
        const file = selectedFiles.value[i];
        const record = await uploadAttachment(receiptId, file, {
          isGuest,
          onProgress: (loaded, total) => {
            attachmentProgress.value[i] = total > 0 ? (loaded / total) * 100 : 0;
          },
        });
        attachmentProgress.value[i] = 100;
        uploaded.push(record);
      }
      const fragment = buildAttachmentFragment(uploaded);
      if (fragment) storeAttachmentFragment(receiptId, fragment);
    } finally {
      uploadingAttachments.value = false;
    }
  }

  // Helper function to get validation errors
  const getError = (field: keyof typeof form) => validation.errors.get(field);

  // Generate unique IDs for form fields to ensure proper label associations
  const uniqueId = computed(() => `secret-form-${Math.random().toString(36).substring(2, 9)}`);
  const passphraseId = computed(() => `passphrase-${uniqueId.value}`);
  const passphraseErrorId = computed(() => `passphrase-error-${uniqueId.value}`);
  const lifetimeId = computed(() => `lifetime-${uniqueId.value}`);
  const lifetimeErrorId = computed(() => `lifetime-error-${uniqueId.value}`);
  const recipientId = computed(() => `recipient-${uniqueId.value}`);
  const recipientErrorId = computed(() => `recipient-error-${uniqueId.value}`);

  const { form, validation, operations, isSubmitting, submit } = useSecretConcealer({
    onSuccess: async (response) => {
      if (!response) throw 'Response is missing';
      const newMessage: LocalReceipt = {
        id: nanoid(),
        receiptExtid: response.record.receipt.identifier,
        receiptShortid: response.record.receipt.shortid,
        secretExtid: response.record.secret.identifier,
        secretShortid: response.record.secret.shortid,
        shareDomain: response.record.share_domain,
        hasPassphrase: !!form.passphrase,
        ttl: form.ttl as number,
        createdAt: Date.now(),
      };
      // Upload any E2EE attachments to the fresh receipt BEFORE we reset/navigate
      // (the progress UI stays mounted meanwhile). The secret itself always
      // succeeds; a FAILED upload must NOT be silent — surface it and keep the
      // selection so the outcome is visible (the link just won't carry the file).
      const receiptId = response.record.receipt.identifier;
      let attachmentOk = true;
      try {
        await uploadSelectedAttachments(receiptId);
      } catch (err) {
         
        console.error('[attachments] upload failed', err);
        // Must NOT be silent: the user thinks the file went with the link.
        attachmentOk = false;
        notifications.show(t('web.homepage.attach_upload_failed'), 'error', 'top');
      }
      if (attachmentOk) selectedFiles.value = [];

      // Add the message to the store
      localReceiptStore.addReceipt(newMessage);
      operations.reset();
      secretContentInput.value?.clearTextarea(); // Clear textarea

      // Emit event for parent components
      emit('created', newMessage);

      // In workspace mode, stay on page; otherwise navigate to receipt. The
      // secret exists either way; on an attachment failure the toast above warns
      // the user that the link won't carry the file.
      if (!props.workspaceMode) {
        router.push(`/receipt/${receiptId}`);
      }
    },
  });

  const {
    state,
    lifetimeOptions,
    updatePassphrase,
    updateTtl,
    updateRecipient,
    togglePassphraseVisibility,
  } = usePrivacyOptions(operations);

  const { currentContext, isContextActive } = useDomainContext();

  // Compute whether the form has content or not
  const hasContent = computed(() => !!form.secret && (form.secret as string).trim().length > 0);

  // Form submission handlers
  const handleSubmit = () => {
    // Use appropriate submission type based on selected action
    if (selectedAction.value === 'generate-password') {
      return submit('generate');
    }
    return submit('conceal');
  };
  const secretContentInput = ref<{ clearTextarea: () => void } | null>(null);
  const selectedAction = ref<'create-link' | 'generate-password'>('create-link');

  // Platform detection for keyboard hint (desktop only)
  const isDesktop = useMediaQuery('(min-width: 640px)');
  const isMac = computed(() =>
    typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform)
  );
  const shortcutHint = computed(() => (isMac.value ? '⌘ Enter' : 'Ctrl Enter'));

  // Resolve the effective share domain for the form.
  // The domain context composable works for authenticated users but for
  // guest users on custom domains it falls back to canonical (no org
  // context). In that case, use display_domain from the bootstrap store.
  const effectiveShareDomain = computed(() => {
    const contextDomain = currentContext.value.domain;
    if (domainStrategy.value === 'custom' && displayDomain.value) {
      return displayDomain.value;
    }
    return contextDomain;
  });

  watch(
    effectiveShareDomain,
    (domain) => {
      if (domain) {
        operations.updateField('share_domain', domain);
      }
    },
    { immediate: true }
  );

  // On a custom domain the form can only ever target that one domain, so the
  // "Creating links for <domain>" badge is noise. Only surface it where the
  // domain context is an actual choice (the workspace multi-domain case).
  const showDomainContext = computed(
    () => isContextActive.value && domainStrategy.value !== 'custom'
  );

  // Focus management when switching between Create Link and Generate Password modes
  const generatePasswordSection = ref<HTMLElement | null>(null);

  // Terminal identity when framed by the landing TerminalWindow: mono
  // micro-labels and near-black fields (the mock's #0A0A10-on-#0C0C12
  // hierarchy), square corners. Default mode keeps the classic card look.
  const labelClass = computed(() =>
    props.chromeless
      ? 'mb-1 block font-mono text-[10px] uppercase tracking-[0.12em] text-day-ink-muted dark:text-night-ink-muted'
      : 'mb-2 block font-mono text-[11px] uppercase tracking-[0.08em] text-day-ink-muted dark:text-night-ink-muted'
  );
  const fieldClass = computed(() =>
    props.chromeless ? '!rounded-none dark:!border-white/[0.13] dark:!bg-night' : ''
  );
</script>

<template>
  <div class="mx-auto max-w-2xl min-w-[320px] space-y-6">
    <!-- Enhanced Alert Display -->
    <BasicFormAlerts
      :errors="Array.from(validation.errors.values())"
      class="sticky top-4 z-50" />

    <form
      ref="form1"
      @submit.prevent="handleSubmit"
      :aria-busy="isSubmitting"
      class="space-y-6"
      data-testid="secret-form">
      <!-- prettier-ignore-attribute class -->
      <!--
        Card radius is fixed (rounded-lg), NOT the brand cornerClass. A large
        brand radius (now capped at xl / 64px since the 9999px `full` preset was
        removed) still reads as an over-rounded blob on this large container. The
        corner token belongs on the controls inside (inputs, select, button),
        matching BaseSecretDisplay's fixed-card / branded-controls split.
      -->
      <div
        ref="div1"
        :class="
          props.chromeless
            ? 'overflow-visible bg-transparent'
            : `overflow-visible rounded-lg border border-day-line bg-day-100
               dark:border-white/[0.13] dark:bg-night-100`
        ">
        <!-- Main Content Section -->
        <div :class="props.chromeless ? 'p-4 sm:p-5' : 'p-8'">
          <!-- Secret Input Section -->
          <span v-show="selectedAction === 'create-link'">
            <!--
              The secret content textarea is labelled by its own aria-label
              (web.secrets.enter_the_secret_content_here, set inside
              SecretContentInputArea). A separate sr-only <label> was removed
              because it had no associated control (invalid label semantics)
              and would have been overridden by that aria-label anyway.
            -->
            <SecretContentInputArea
              ref="secretContentInput"
              v-model:content="form.secret"
              :disabled="isSubmitting"
              :max-height="400"
              :corner-class="cornerClass"
              :field-class="fieldClass"
              @update:content="(content) => operations.updateField('secret', content)" />

            <!-- E2EE file attachment picker: only for the create-link action and
                 only when object storage is configured. Files are encrypted in
                 the browser and uploaded after the secret is created. -->
            <AttachmentPicker
              v-if="attachmentsEnabled && selectedAction === 'create-link'"
              v-model="selectedFiles"
              :progress="attachmentProgress"
              :uploading="uploadingAttachments"
              :max-size-bytes="attachmentMaxSize"
              :disabled="isSubmitting && !uploadingAttachments"
              class="mt-3" />
          </span>

          <!-- Generate Password Text -->
          <!-- Fixed rounded-lg (large container); see the outer card note above. -->
          <div
            v-show="selectedAction === 'generate-password'"
            class="relative overflow-hidden rounded-lg border border-brand-200/60 bg-brand-50 dark:border-brand-700/40 dark:bg-brand-900/20"
            aria-labelledby="generatedPasswordHeader"
            aria-describedby="generatedPasswordDesc"
            role="region"
            ref="generatePasswordSection">
            <!-- Decorative blur orbs -->
            <div
              class="hidden"
              aria-hidden="true"></div>
            <div
              class="hidden"
              aria-hidden="true"></div>

            <div class="relative z-10 space-y-4 p-8 text-center">
              <div class="flex justify-center">
                <div
                  class="rounded-full bg-brand-100 p-4 dark:bg-brand-900/40">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    class="size-7 text-brand-600 dark:text-brand-400">
                    <path
                      d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
                  </svg>
                </div>
              </div>

              <h4
                id="generatedPasswordHeader"
                class="font-brand text-lg font-semibold tracking-[-0.01em] text-day-ink dark:text-night-ink"
                tabindex="-1">
                {{ t('web.homepage.password_generation_title') }}
              </h4>

              <p
                id="generatedPasswordDesc"
                class="mx-auto max-w-md text-day-ink-muted dark:text-night-ink-muted">
                {{ t('web.homepage.password_generation_description') }}
              </p>
            </div>
          </div>

          <!-- Form Controls Section -->
          <div class="mt-8 grid gap-6 md:grid-cols-2 md:items-start">
            <!-- Passphrase Field -->
            <div class="relative">
              <label
                :for="passphraseId"
                :class="labelClass">
                {{ t('web.COMMON.secret_passphrase') }}
                <span
                  v-if="isPassphraseRequired"
                  class="ml-1 text-red-500"
                  aria-label="Required">*</span>
              </label>
              <!-- Fixed height container for hints to prevent layout shifts -->
              <div class="mb-2 min-h-4">
                <div
                  v-if="passphraseConfig"
                  class="text-xs text-day-ink-faint dark:text-night-ink-faint">
                  <span v-if="passphraseConfig.minimum_length">
                    {{
                      t('web.secrets.passphraseMinimumLength', {
                        length: passphraseConfig.minimum_length,
                      })
                    }}
                  </span>
                  <span
                    v-if="passphraseConfig.minimum_length && passphraseConfig.enforce_complexity">
                    •
                  </span>
                  <span v-if="passphraseConfig.enforce_complexity">
                    {{ t('web.secrets.passphraseComplexityRequired') }}
                  </span>
                </div>
              </div>
              <div class="relative">
                <!-- prettier-ignore-attribute class -->
                <input
                  :type="state.passphraseVisibility ? 'text' : 'password'"
                  :value="form.passphrase"
                  :id="passphraseId"
                  name="passphrase"
                  autocomplete="off"
                  :aria-invalid="!!getError('passphrase')"
                  :aria-errormessage="getError('passphrase') ? passphraseErrorId : undefined"
                  :class="[
                    cornerClass,
                    fieldClass,
                    getError('passphrase')
                      ? 'border-red-500/50 focus:border-red-500 focus:ring-red-500/20'
                      : '',
                  ]"
                  class="w-full border border-day-line bg-day py-2.5 pr-10 pl-5 text-sm
                    text-day-ink transition-all duration-300 placeholder:text-day-ink-faint
                    hover:border-day-line-2
                    focus:border-brand-500 focus:bg-day focus:ring-4 focus:ring-brand-500/20 focus:outline-none
                    dark:border-white/[0.13] dark:bg-night-100 dark:text-night-ink dark:placeholder:text-night-ink-faint
                    dark:hover:border-white/20
                    dark:focus:border-brand-500 dark:focus:bg-night-100 dark:focus:ring-brand-500/20"
                  :placeholder="t('web.secrets.enterPassphrase')"
                  @input="(e) => updatePassphrase((e.target as HTMLInputElement).value)"
                  data-testid="secret-passphrase-input" />
                <!-- prettier-ignore-attribute class -->
                <button
                  type="button"
                  @click="togglePassphraseVisibility"
                  :aria-label="state.passphraseVisibility ? 'Hide passphrase' : 'Show passphrase'"
                  :aria-pressed="state.passphraseVisibility"
                  class="absolute inset-y-0 right-3 flex items-center
                    focus:ring-2 focus:ring-brand-500 focus:outline-none"
                  data-testid="secret-toggle-passphrase">
                  <OIcon
                    collection="heroicons"
                    :name="state.passphraseVisibility ? 'solid-eye' : 'outline-eye-off'"
                    class="size-4 text-day-ink-faint hover:text-day-ink-muted dark:text-night-ink-faint dark:hover:text-night-ink-muted"
                    aria-hidden="true" />
                </button>
              </div>
            </div>

            <!-- Expiry Selection -->
            <div
              v-if="props.withExpiry"
              class="relative">
              <label
                :for="lifetimeId"
                :class="labelClass">
                {{ t('web.LABELS.expiration_time') || 'Secret Expiration' }}
              </label>
              <!-- Empty spacer to match passphrase field hint area -->
              <div class="mb-2 min-h-4"></div>
              <div class="relative">
                <!-- prettier-ignore-attribute class -->
                <select
                  :value="form.ttl"
                  :id="lifetimeId"
                  name="ttl"
                  :aria-invalid="!!getError('ttl')"
                  :aria-describedby="getError('ttl') ? lifetimeErrorId : undefined"
                  :class="[
                    cornerClass,
                    fieldClass,
                    getError('ttl')
                      ? 'border-red-500/50 focus:border-red-500 focus:ring-red-500/20'
                      : '',
                  ]"
                  class="w-full appearance-none border border-day-line
                    bg-day py-2.5 pr-10 pl-5 text-sm text-day-ink transition-all duration-300
                    hover:border-day-line-2
                    focus:border-brand-500 focus:bg-day focus:ring-4 focus:ring-brand-500/20 focus:outline-none
                    dark:border-white/[0.13] dark:bg-night-100 dark:text-night-ink
                    dark:hover:border-white/20
                    dark:focus:border-brand-500 dark:focus:bg-night-100 dark:focus:ring-brand-500/20"
                  @change="(e) => updateTtl(Number((e.target as HTMLSelectElement).value))"
                  data-testid="secret-ttl-select">
                  <option
                    value=""
                    disabled>
                    {{ t('web.secrets.selectDuration') }}
                  </option>
                  <template v-if="lifetimeOptions.length > 0">
                    <option
                      v-for="option in lifetimeOptions"
                      :key="option.value"
                      :value="option.value">
                      {{ t('web.secrets.expiresIn', { duration: option.label }) }}
                    </option>
                  </template>
                  <option
                    v-else
                    value=""
                    disabled>
                    {{ t('web.UNITS.ttl.noOptionsAvailable') }}
                  </option>
                </select>
              </div>
            </div>
          </div>

          <!-- Recipient Field -->
          <div
            v-if="showRecipient"
            class="mt-8">
            <label
              :for="recipientId"
              :class="labelClass">
              {{ t('web.COMMON.secret_recipient_address') || 'Email Recipient' }}
            </label>
            <div class="relative">
              <div class="pointer-events-none absolute inset-y-0 left-3 flex items-center">
                <OIcon
                  collection="heroicons"
                  name="envelope"
                  size="5"
                  class="text-day-ink-faint dark:text-night-ink-faint"
                  aria-hidden="true" />
              </div>
              <!-- prettier-ignore-attribute class -->
              <input
                :id="recipientId"
                :value="form.recipient"
                type="email"
                name="recipient[]"
                autocomplete="email"
                :placeholder="t('web.COMMON.email_placeholder')"
                :aria-invalid="!!getError('recipient')"
                :aria-errormessage="getError('recipient') ? recipientErrorId : undefined"
                :class="[
                  cornerClass,
                  getError('recipient')
                    ? 'border-red-500/50 focus:border-red-500 focus:ring-red-500/20'
                    : '',
                ]"
                class="w-full border border-day-line bg-day py-2.5
                  pr-4 pl-10 text-sm text-day-ink transition-colors
                  duration-200 placeholder:text-day-ink-faint
                  hover:border-day-line-2
                  focus:border-brand-500 focus:bg-day focus:ring-4 focus:ring-brand-500/20 focus:outline-none
                  dark:border-white/[0.13] dark:bg-night-100 dark:text-night-ink dark:placeholder:text-night-ink-faint
                  dark:hover:border-white/20
                  dark:focus:border-brand-500 dark:focus:bg-night-100 dark:focus:ring-brand-500/20"
                @input="(e) => updateRecipient((e.target as HTMLInputElement).value)"
                data-testid="secret-recipient-input" />
            </div>
          </div>
        </div>

        <!-- Pro tip Section -->
        <div
          v-if="showProTip"
          class="relative flex items-start gap-3 overflow-hidden p-5"
          :class="
            props.chromeless
              ? 'border-t border-gray-200 bg-gray-50 dark:border-white/[0.07] dark:bg-night-200'
              : 'border-t border-day-line bg-day-200 dark:border-white/[0.07] dark:bg-night-200'
          ">
          <!-- Decorative blur orb (card mode only — the terminal is flat) -->
          <div
            v-if="!props.chromeless"
            class="hidden"
            aria-hidden="true"></div>
          <OIcon
            collection="heroicons"
            name="information-circle"
            class="relative z-10 mt-0.5 size-5 shrink-0"
            :class="
              props.chromeless
                ? 'text-brand-600 dark:text-brand-500'
                : 'text-brand-600 dark:text-brand-500'
            " />
          <p
            class="relative z-10 text-sm"
            :class="
              props.chromeless
                ? 'text-gray-600 dark:text-night-ink-muted'
                : 'text-day-ink-muted dark:text-night-ink-muted'
            ">
            {{ t('web.homepage.protip1') }}
          </p>
        </div>

        <div
          :class="
            props.chromeless
              ? 'border-t border-gray-200 dark:border-white/[0.13]'
              : 'border-t border-day-line dark:border-white/[0.13]'
          ">
          <!-- Actions Container -->
          <div :class="props.chromeless ? 'p-4 sm:p-5' : 'p-8'">
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between sm:gap-4">
              <!-- Domain Context Indicator -->
              <div
                v-if="showDomainContext"
                class="order-1 flex items-center gap-2 text-sm sm:order-1">
                <span class="text-day-ink-muted dark:text-night-ink-muted">
                  {{ t('web.LABELS.creating_links_for') }}
                </span>
                <div
                  class="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-all duration-150"
                  :class="
                    currentContext.isCanonical
                      ? 'bg-day-200 text-day-ink-muted dark:bg-night-200 dark:text-night-ink-muted'
                      : 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300'
                  "
                  role="status"
                  :aria-label="
                    t('web.LABELS.scope_indicator', { domain: currentContext.displayName })
                  "
                  data-testid="secret-domain-context-indicator">
                  <OIcon
                    collection="heroicons"
                    :name="currentContext.isCanonical ? 'user-circle' : 'building-office'"
                    class="size-4"
                    aria-hidden="true" />
                  <span class="max-w-[180px] truncate">{{ currentContext.displayName }}</span>
                </div>
              </div>

              <!-- Action Button (full-width on mobile, normal width on desktop) -->
              <div class="order-2 shrink-0 sm:order-2 sm:ml-auto">
                <div class="mt-3 mb-0 sm:mt-0">
                  <SplitButton
                    :with-generate="props.withGenerate"
                    :corner-class="cornerClass"
                    :primary-color="primaryColor"
                    :button-text-light="buttonTextLight"
                    :disabled="selectedAction === 'create-link' && !hasContent"
                    :disable-generate="selectedAction === 'create-link' && hasContent"
                    :keyboard-shortcut-enabled="true"
                    :show-keyboard-hint="false"
                    :aria-label="
                      selectedAction === 'create-link' ? 'Create Secret Link' : 'Generate Password'
                    "
                    :aria-describedby="
                      selectedAction === 'create-link'
                        ? 'create-link-desc'
                        : 'generate-password-desc'
                    "
                    @update:action="selectedAction = $event" />
                  <div
                    class="sr-only"
                    id="create-link-desc">
                    Creates a secure link to share your secret
                  </div>
                  <div
                    class="sr-only"
                    id="generate-password-desc">
                    Generates a secure random password
                  </div>
                </div>
              </div>
            </div>

            <!-- Keyboard hint row (desktop only) -->
            <div
              v-if="isDesktop"
              class="mt-3 flex justify-end">
              <span class="font-mono text-xs text-day-ink-faint dark:text-night-ink-faint">
                {{ shortcutHint }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>
